import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const LS_KEY = 'vusub_isagency';

const PermissionsContext = createContext({ permissions: {}, loading: true, refetch: () => {} });

export const usePermissions = () => useContext(PermissionsContext);

// Called on logout to clear cached permissions
export const clearPermissionsCache = () => {
  try { localStorage.removeItem(LS_KEY); } catch {}
};

export const PermissionsProvider = ({ children }) => {
  const { instance, accounts } = useMsal();

  // Seed initial state from localStorage so isAgency is true on page refresh
  // without waiting for an API call.
  const [permissions, setPermissions] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) === '1' ? { isAgency: true } : {};
    } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const account = accounts[0];
    if (!account) {
      setLoading(false);
      return;
    }
    try {
      let result = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile'],
        account,
      });
      // idToken can be null/empty on silent refresh — force a fresh one if so
      if (!result.idToken) {
        result = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile'],
          account,
          forceRefresh: true,
        });
      }
      if (!result.idToken) {
        setLoading(false);
        return;
      }
      // Use apply/status — proven to work, checks userpermissions table
      const resp = await fetch(`${API_URL}/api/agency/apply/status`, {
        headers: { Authorization: `Bearer ${result.idToken}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        const isAgency = data.status === 'approved';
        const perms = { isAgency };
        setPermissions(perms);
        try { localStorage.setItem(LS_KEY, isAgency ? '1' : '0'); } catch {}
      }
    } catch (err) {
      console.warn('[PermissionsContext] fetchPermissions error:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionsContext.Provider value={{ permissions, loading, refetch: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};
